import type { KeyboardEvent } from 'react';
import { useCallback, useEffect, useRef } from 'react';
import type { AutocompleteOption } from '../types/index';

export function useAutocompleteKeyDown({
    options,
    selected,
    setSelected,
    setOptions,
    setNoResults,
    selectCurrentOption,
}: {
    options: AutocompleteOption[];
    selected: number;
    setSelected: (index: number) => void;
    setOptions: (opts: AutocompleteOption[]) => void;
    setNoResults: (val: boolean) => void;
    selectCurrentOption: () => void;
}) {
    const selectedRef = useRef(selected);

    useEffect(() => {
        selectedRef.current = selected;
    }, [selected]);

    return useCallback(
        (e: KeyboardEvent<HTMLInputElement>) => {
            const key = e.key;

            const current = selectedRef.current;
            const hasSelection = current !== -1;
            const lastIndex = options.length - 1;
            const next = (current + 1) % options.length;
            const prev = current <= 0 ? lastIndex : current - 1;

            switch (key) {
                case 'ArrowDown':
                    setSelected(next);
                    e.preventDefault();
                    break;

                case 'ArrowUp':
                    setSelected(prev);
                    e.preventDefault();
                    break;

                case 'Enter':
                    if (hasSelection) {
                        selectCurrentOption();
                    }
                    break;

                case 'Tab':
                    if (hasSelection) {
                        selectCurrentOption();
                    }
                    break;

                case 'Home':
                    setSelected(0);
                    e.preventDefault();
                    break;

                case 'End':
                    setSelected(lastIndex);
                    e.preventDefault();
                    break;

                case 'Escape':
                    setOptions([]);
                    setSelected(-1);
                    setNoResults(false);
                    break;

                default:
                    break;
            }
        },
        [options.length, setSelected, setOptions, setNoResults, selectCurrentOption],
    );
}
